import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SeasonProcessState, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { SeasonPlan } from '@coin/shared/core/util-models';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Season } from '@coin/shared/season-mgmt/util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeasonSimulation } from '../models/season-simulation.model';
import { SeasonState } from '../store/season.state';

@Injectable({
  providedIn: 'root'
})
export class ProcessNavigationService {
  private store = inject(Store);
  private router = inject(Router);
  private toast = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  currentSeason$ = this.store.select<Season>(SeasonState.currentSeason);
  currentPlan$ = this.store.select<SeasonPlan>(SeasonState.currentPlan);
  currentSim$ = this.store.select<SeasonSimulation>(SeasonState.currentSimulation);

  currentSeason: Season;
  currentPlan: SeasonPlan;
  currentSim: SeasonSimulation;

  get seasonType(): SeasonTypeEnum {
    return [SeasonTypeEnum.SpecialPaymentCash, SeasonTypeEnum.SpecialPaymentEquity].includes(
      this.currentSeason.seasonType
    )
      ? SeasonTypeEnum.SpecialPayment
      : this.currentSeason.seasonType;
  }

  constructor() {
    this.currentSeason$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(season => {
      this.currentSeason = season;
    });
    this.currentPlan$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(plan => {
      this.currentPlan = plan;
    });
    this.currentSim$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sim => {
      this.currentSim = sim;
    });
  }

  nextProcessStep(): void {
    const nextStep = this.store.selectSnapshot(SeasonState.nextProcessStep);
    this.navigate(nextStep);
  }

  prevProcessStep(): void {
    const prevStep = this.store.selectSnapshot(SeasonState.prevProcessStep);
    this.navigate(prevStep);
  }

  goToStep(id: string): void {
    const step = this.store.selectSnapshot(SeasonState.getProcessStepById(id));
    if (step) {
      this.navigate(step);
    }
  }

  navigate(step: { route: string; id?: SeasonProcessState }): void {
    if (!step?.route || !this.currentSeason?.id) {
      this.toast.error('Cannot open season.');
      return;
    }

    const noSeasonIdParam = step?.id === SeasonProcessState.SeasonOverview;

    this.router.navigate([step.route], {
      queryParams: {
        seasonId: !noSeasonIdParam ? this.currentSeason.id : undefined,
        planId:
          !this.currentPlan?.id || step.id === SeasonProcessState.PlanManagement ? undefined : this.currentPlan.id,
        simId:
          !this.currentSim?.id || step.id === SeasonProcessState.SimulationManagement ? undefined : this.currentSim.id,
        type: this.seasonType
      }
    });
  }
}
